import React, { useState, useRef, useEffect } from "react";
import { Link, useLocation, NavLink } from "react-router-dom";
import { Bell, User, Grid, X, LogOut, Settings, HelpCircle, User as UserIcon, ShoppingBag, Heart } from "lucide-react";
import { LayoutDashboard, Package, Wallet, ShoppingCart, CreditCard, Handshake, Headphones, LifeBuoy, ArrowLeft, ChevronRight} from "lucide-react";
import NotificationPanelMobile from "../../../app/header/sub/mobile/NotificationPanelMobile";

interface HeaderMobileProps {
  searchQuery: string;
  setSearchQuery: (value: string) => void;
  isSearchVisible: boolean;
  setIsSearchVisible: (value: boolean) => void;
  setIsMenuOpen: (value: boolean) => void;
  handleSearch: (e: React.FormEvent) => void;
  scrolled: boolean;
}

// Tous les éléments pour le popup "Plus"
const NavItems = [
  { name: 'Dashboard', path: '/dashboard', icon: LayoutDashboard },
  { name: 'Commandes', path: '/dashboard/commandes', icon: Package },
  { name: 'Portefeuille', path: '/dashboard/portefeuille', icon: Wallet },
  { name: 'Panier', path: '/dashboard/panier', icon: ShoppingCart },
  { name: 'Transactions', path: '/dashboard/transactions', icon: CreditCard },
  { name: 'Favoris', path: '/dashboard/favoris', icon: Heart },
  { name: 'Notifications', path: '/dashboard/notifications', icon: Bell },
  { name: 'Partenariat', path: '/dashboard/partenariat', icon: Handshake },
  { name: 'Profil', path: '/dashboard/profil', icon: User },
  { name: 'Paramètres', path: '/dashboard/parametres', icon: Settings },
  { name: 'Support', path: '/dashboard/support', icon: Headphones },
  { name: "Centre d'aide", path: '/dashboard/aide', icon: LifeBuoy },
];

const HeaderMobileDashboard: React.FC<HeaderMobileProps> = ({
  searchQuery,
  setSearchQuery,
  isSearchVisible,
  setIsSearchVisible,
  setIsMenuOpen,
  handleSearch,
  scrolled,
}) => {
  const [isNotificationsVisible, setIsNotificationsVisible] = useState(false);
  const [unreadNotificationsCount] = useState(2);
  const [isMoreOpen, setIsMoreOpen] = useState(false);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const location = useLocation();
  const profileButtonRef = useRef<HTMLButtonElement>(null);
  const profilePopoverRef = useRef<HTMLDivElement>(null);

  const openMore = () => setIsMoreOpen(true);
  const closeMore = () => setIsMoreOpen(false);
  const handleBellClick = () => setIsNotificationsVisible(true);
  const handleNavClick = () => setIsMoreOpen(false);
  const toggleProfile = () => setIsProfileOpen(!isProfileOpen);

  // Fermer le popup profil au clic en dehors
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (profilePopoverRef.current && !profilePopoverRef.current.contains(event.target as Node) &&
        profileButtonRef.current && !profileButtonRef.current.contains(event.target as Node)) {
        setIsProfileOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <>
      <header
        className={`lg:hidden fixed top-0 left-0 right-0 z-40 transition-all duration-300 ${scrolled
          ? "bg-white/95 backdrop-blur-md shadow-lg border-b border-gray-100"
          : "bg-white border-b border-gray-100"
          }`}
      >
        <div className="px-4 pt-4">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-2">
              <img
                src="/assets/logo/notextfavicon.jpeg"
                alt="Kayovex"
                className="w-8 h-8 object-contain rounded-lg"
              />
              <div className="flex flex-col">
                <span className="text-sm font-semibold text-gray-800">Kayovex</span>
                <span className="text-[10px] text-gray-500 -mt-0.5">Marketplace</span>
              </div>
            </Link>

            {/* Boutons actions */}
            <div className="flex items-center gap-2">
              <button
                onClick={openMore}
                className="relative rounded-full bg-gradient-to-r from-primary/10 to-secondary/10 border border-primary/20 shadow-sm hover:shadow-md transition-all duration-200 active:scale-95"
              >
                <div className="p-2 rounded-full">
                  <Grid size={20} className="text-primary" />
                </div>
              </button>

              <button
                onClick={handleBellClick}
                className="relative p-2 rounded-full bg-gradient-to-r from-primary/10 to-secondary/10 border border-primary/20 shadow-sm hover:shadow-md transition-all duration-200 active:scale-95"
                aria-label="Notifications"
              >
                <Bell className="w-[18px] h-[18px] text-primary" />
                {unreadNotificationsCount > 0 && (
                  <>
                    <span className="absolute top-0 right-0 w-2.5 h-2.5 bg-red-500 rounded-full animate-pulse ring-2 ring-white" />
                    <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[9px] font-bold min-w-[16px] h-[16px] flex items-center justify-center rounded-full px-0.5 shadow-sm">
                      {unreadNotificationsCount > 9 ? "9+" : unreadNotificationsCount}
                    </span>
                  </>
                )}
              </button>

              <button
                ref={profileButtonRef}
                onClick={toggleProfile}
                className="p-2 rounded-full bg-gradient-to-r from-primary/10 to-secondary/10 border border-primary/20 shadow-sm hover:shadow-md transition-all duration-200 active:scale-95"
              >
                <User className="w-[18px] h-[18px] text-primary" />
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="h-[60px] lg:hidden" />

      <NotificationPanelMobile
        isNotificationsVisible={isNotificationsVisible}
        setIsNotificationsVisible={setIsNotificationsVisible}
      />

      
      

      {/* Popup "Plus" - style overlay avec backdrop-blur */}
      <div
        className={`fixed inset-0 flex items-end transition-all duration-300 z-50 ${isMoreOpen ? "pointer-events-auto" : "opacity-0 pointer-events-none"
          }`}
        onClick={closeMore}
      >
        {/* Overlay avec blur */}
        <div className="absolute inset-0 h-screen backdrop-blur-[4px] bg-primary/50" />

        {/* Panneau central */}
        <div
          className={`w-full rounded-t-2xl p-4 shadow-sm transform transition-transform duration-300 h-full overflow-y-auto ${isMoreOpen ? "translate-y-0" : "translate-y-full"
            }`}
          style={{ backgroundColor: "#ffffff" }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* En-tête avec bouton retour */}
          <div className="flex items-center mb-4">
            <button
              onClick={closeMore}
              className="p-2 transition rounded-full hover:bg-gray-100"
            >
              <ArrowLeft size={20} className="text-black" />
            </button>
            <h4 className="text-lg font-semibold text-black ml-2">Retour</h4>
          </div>

          {/* Liste des options (NavItems) */}
          <div className="flex flex-col">
            {NavItems.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                end={item.path === '/dashboard'}
                onClick={() => {
                  closeMore();
                  // facultatif : ajouter un scroll ou autre
                }}
                className="flex items-center justify-between px-4 py-3 transition rounded-xl hover:bg-gray-100"
              >
                <div className="flex items-center gap-3">
                  <div className="flex items-center justify-center bg-gray-100 rounded-full w-9 h-9">
                    <item.icon size={18} className="text-gray-600" />
                  </div>
                  <span className="font-medium text-gray-800">{item.name}</span>
                </div>
                <ChevronRight size={20} className="text-gray-400" />
              </NavLink>
            ))}

            {/* Carte Centre d'aide */}
            <div className="mt-6 p-3">
              <div className="relative overflow-hidden text-white shadow-md rounded-2xl bg-gradient-to-r from-primary to-secondary">
                <div className="absolute inset-0 bg-white opacity-10 rounded-2xl"></div>
                <div className="relative z-10 p-4">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-white/20">
                      <LifeBuoy className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold">Centre d'aide</h3>
                      <p className="text-xs text-white/80">Questions fréquentes & support</p>
                    </div>
                  </div>
                  <NavLink to="/dashboard/aide" onClick={closeMore}>
                    <button className="w-full py-2 text-sm font-medium transition bg-white text-primary rounded-xl hover:bg-gray-100">
                      Obtenir de l'aide
                    </button>
                  </NavLink>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Overlay (fond noir) qui n'apparaît que si le popup est ouvert */}
      {isMoreOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/40 transition-opacity duration-300 md:hidden"
          onClick={closeMore}
        />
      )}

      {/* Popup Profil (en haut à droite) */}
      {isProfileOpen && (
        <div
          ref={profilePopoverRef}
          className="fixed top-14 right-4 z-50 w-72 bg-white rounded-2xl shadow-2xl border border-gray-100 animate-fadeInDown origin-top-right"
        >
          <div className="p-4 border-b border-gray-100">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-primary to-secondary flex items-center justify-center text-white font-bold text-lg">
                K
              </div>
              <div>
                <p className="font-semibold text-gray-800">Jean Dupont</p>
                <p className="text-xs text-gray-500">jean@kayovex.com</p>
              </div>
            </div>
          </div>
          <div className="p-2">
            <NavLink to="/dashboard/profil" className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-gray-50 transition-colors">
              <UserIcon size={18} className="text-gray-500" />
              <span className="text-sm text-gray-700">Mon profil</span>
            </NavLink>
            <NavLink to="/dashboard/commandes" className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-gray-50 transition-colors">
              <ShoppingBag size={18} className="text-gray-500" />
              <span className="text-sm text-gray-700">Mes commandes</span>
            </NavLink>
            <NavLink to="/dashboard/favoris" className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-gray-50 transition-colors">
              <Heart size={18} className="text-gray-500" />
              <span className="text-sm text-gray-700">Mes favoris</span>
            </NavLink>
            <NavLink to="/dashboard/parametres" className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-gray-50 transition-colors">
              <Settings size={18} className="text-gray-500" />
              <span className="text-sm text-gray-700">Paramètres</span>
            </NavLink>
            <NavLink to="/dashboard/support" className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-gray-50 transition-colors">
              <HelpCircle size={18} className="text-gray-500" />
              <span className="text-sm text-gray-700">Aide</span>
            </NavLink>
          </div>
          <div className="p-3 border-t border-gray-100">
            <button className="flex items-center gap-3 w-full px-3 py-2 rounded-lg text-red-600 hover:bg-red-50 transition-colors">
              <LogOut size={18} />
              <span className="text-sm font-medium">Déconnexion</span>
            </button>
          </div>
        </div>
      )}

      <style>{`
        .custom-scrollbar-popup::-webkit-scrollbar {
          width: 4px;
        }
        .custom-scrollbar-popup::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 10px;
        }
        .custom-scrollbar-popup::-webkit-scrollbar-thumb {
          background: #ff077e;
          border-radius: 10px;
        }
        @keyframes fadeInDown {
          from {
            opacity: 0;
            transform: translateY(-20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-fadeInDown {
          animation: fadeInDown 0.2s ease-out;
        }
        .duration-400 {
          transition-duration: 400ms;
        }
      `}</style>
    </>
  );
};

export default HeaderMobileDashboard;